import {
  useModalContext,
  WSButton,
  WSDivider,
  WSElement,
  WSForm,
  WSIconList,
  WSModal,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import { IRedactedMember } from "@wingspanhq/users";
import React, { useState } from "react";
import * as Yup from "yup";
import logo from "../../../assets/images/ws-logo.svg";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import {
  useCreateClientWithoutUserId,
  useCreateSession,
  useCreateSessionWithGoogleAuthToken,
  useCreateUser
} from "../../../query/users/mutations";
import { getRedactedUserName } from "../../../query/users/selectors";
import { GrowthSourceNames } from "../../../utils/growthAttribution";
import {
  validatorSignInEmail,
  validatorSignInPassword
} from "../../../utils/validators";
import { useWSAnalytics } from "../../../utils/WSAnalytics";
import { GoogleAuthButton } from "../GoogleAuthButton/GoogleAuthButton";
import styles from "./ClientSignInModal.module.scss";

export const CLIENT_SIGN_IN_MODAL_KEY = "clientSignInModal";

export const ClientSignInModal: React.FC = () => {
  return (
    <WSModal name={CLIENT_SIGN_IN_MODAL_KEY} size="S" p="M">
      {(props: SignInModalProps) => <Inner {...props} />}
    </WSModal>
  );
};

type SignInModalProps = {
  clientEmailToPrefill?: string;
  onSuccess?: () => void;
  signUp?: boolean;
  signUpAsMember?: boolean;
  memberId?: string;
  member?: IRedactedMember;
};

const Inner: React.FC<SignInModalProps> = ({
  clientEmailToPrefill,
  onSuccess,
  signUp,
  signUpAsMember,
  memberId,
  member
}) => {
  const analytics = useWSAnalytics();

  const [createUser, createUserMeta] = useCreateUser();
  const [createSession, createSessionMeta] = useCreateSession();
  const [createClient, createClientMeta] = useCreateClientWithoutUserId();
  const [
    createSessionWithGoogleAuthToken,
    createSessionWithGoogleAuthTokenMeta
  ] = useCreateSessionWithGoogleAuthToken();
  const { closeModal } = useModalContext();
  const [isSignUp, setIsSignUp] = useState(!!signUp || !!signUpAsMember);
  const [formError, setFormError] = useState("");

  const growthAttributionDetails = {
    growthSource: GrowthSourceNames.Invoice,
    growthName: member ? getRedactedUserName(member.user) : undefined,
    growthUserId: memberId
  };

  const onSubmit = async (data: any) => {
    if (isSignUp) {
      createUser(data, {
        onSuccess: () => {
          analytics.track.signUp({
            growthAttributionDetails,
            signUpMethod: "Email"
          });
          createSession(data, {
            onSuccess: session => {
              const userId = session.userId as string;
              createClient(
                { userId, data: { userId } },
                {
                  onSuccess: () => {
                    closeModal(CLIENT_SIGN_IN_MODAL_KEY);
                    onSuccess?.();

                    if (signUpAsMember) {
                      window.open("/member/onboarding/customizing", "_self");
                    }
                  },
                  onError: error => {
                    if (error.response?.status === 400) {
                      closeModal(CLIENT_SIGN_IN_MODAL_KEY);
                      onSuccess?.();
                    }
                  }
                }
              );
            }
          });
        }
      });
    } else {
      createSession(data, {
        onSuccess: () => {
          closeModal(CLIENT_SIGN_IN_MODAL_KEY);
          onSuccess?.();
        }
      });
    }
  };

  const imgProps: any = {
    src: logo
  };

  return (
    <>
      <WSElement {...imgProps} as="img" className={styles.logo} />

      <WSDivider my="M" fullScreen />

      <WSText.Heading3 mb="S">
        {isSignUp
          ? "Create a free Wingspan account"
          : "Sign in to your Wingspan account"}
      </WSText.Heading3>
      {isSignUp && (
        <WSIconList.Checks
          items={[
            "Secure, 1-click payments",
            "Enable Autopay to pay on time, every time",
            "Manage multiple freelancers",
            "Automated tax documents"
          ]}
        />
      )}
      <WSElement my="L">
        <GoogleAuthButton
          isLoading={createSessionWithGoogleAuthTokenMeta.isLoading}
          onLoginSuccess={token => {
            createSessionWithGoogleAuthToken(
              {
                idToken: token
              },
              {
                onSuccess: () => {
                  if (isSignUp) {
                    analytics.track.signUp({
                      growthAttributionDetails,
                      signUpMethod: "Google"
                    });
                  }
                  closeModal(CLIENT_SIGN_IN_MODAL_KEY);
                  onSuccess?.();

                  if (signUpAsMember) {
                    window.open("/member/onboarding/customizing", "_self");
                  }
                }
              }
            );
          }}
          onLoginFailure={() => {
            setFormError(
              "Sorry! There was an error signing in with your Google account"
            );
          }}
        >
          {isSignUp ? "Sign up" : "Sign in"} with Google
        </GoogleAuthButton>
      </WSElement>
      <WSDivider
        mt="L"
        label={isSignUp ? "Or create an account" : "Or use your account"}
      />
      <WSForm
        mt="L"
        onSubmit={onSubmit}
        defaultValues={{
          email: clientEmailToPrefill || "",
          password: ""
        }}
        validationSchema={Yup.object().shape({
          email: validatorSignInEmail,
          password: validatorSignInPassword
        })}
      >
        <WSForm.Field
          name="email"
          label="Email"
          mb="M"
          component={WSTextInput}
          componentProps={{
            type: "email"
          }}
        />
        <WSForm.Field
          name="password"
          label="Password"
          mb="M"
          component={WSTextInput}
          componentProps={{
            type: "password"
          }}
        />
        <WSButton.Primary
          fullWidth
          loading={
            createUserMeta.isLoading ||
            createSessionMeta.isLoading ||
            createClientMeta.isLoading
          }
          name={isSignUp ? "signUp" : "signIn"}
        >
          {isSignUp ? "Create account" : "Sign in"}
        </WSButton.Primary>

        <WSErrorMessage
          mb="L"
          contextKey="ClientAuth"
          error={
            formError ||
            createUserMeta.error ||
            createSessionMeta.error ||
            createClientMeta.error
          }
        />
      </WSForm>
      <WSDivider
        mt="L"
        label={isSignUp ? "Have an account?" : "Need an account?"}
      />
      <WSButton.Secondary
        mt="L"
        onClick={() => setIsSignUp(!isSignUp)}
        fullWidth
        name={isSignUp ? "signIn" : "signUp"}
      >
        {isSignUp ? "Sign in" : "Sign up"}
      </WSButton.Secondary>
    </>
  );
};
